import { ReactNode, createContext, useContext, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';

type CourseEditorContextType = {
  isCourseEditorOpened: boolean;
  setIsCourseEditorOpened: (value: boolean) => void;
  isCourseSettingsOpened: boolean;
  openCourseSettings: () => void;
  closeCourseSettings: () => void;
  courseSettingsButtonProps: ReturnType<typeof useDisclosure>['getButtonProps'];
  courseSettingsDisclosure: ReturnType<typeof useDisclosure>['getDisclosureProps'];
  isCourseSettingsClosed: boolean;
  setIsCourseSettingsClosed: (value: boolean) => void;
  isCourseSettingsExpanded: boolean;
  setIsCourseSettingsExpanded: (value: boolean) => void;
};

const CourseEditorContext = createContext<CourseEditorContextType | undefined>(undefined);

export function CourseEditorProvider({ children }: { children: ReactNode }) {
  const {
    getButtonProps: courseSettingsButtonProps,
    getDisclosureProps: courseSettingsDisclosure,
    isOpen: isCourseSettingsOpened,
    onClose: closeCourseSettingsBase,
    onOpen: openCourseSettingsBase,
  } = useDisclosure();

  const [isCourseEditorOpened, setIsCourseEditorOpened] = useState(false);
  const [isCourseSettingsClosed, setIsCourseSettingsClosed] = useState(!isCourseSettingsOpened);
  const [isCourseSettingsExpanded, setIsCourseSettingsExpanded] = useState(true);

  const openCourseSettings = () => {
    openCourseSettingsBase();
  };

  const closeCourseSettings = () => {
    closeCourseSettingsBase();
  };

  return (
    <CourseEditorContext.Provider
      value={{
        isCourseEditorOpened,
        setIsCourseEditorOpened,
        isCourseSettingsOpened,
        openCourseSettings,
        closeCourseSettings,
        courseSettingsButtonProps,
        courseSettingsDisclosure,
        isCourseSettingsClosed,
        setIsCourseSettingsClosed,
        isCourseSettingsExpanded,
        setIsCourseSettingsExpanded,
      }}
    >
      {children}
    </CourseEditorContext.Provider>
  );
}

export const useCourseEditorState = () => {
  const context = useContext(CourseEditorContext);
  if (context === undefined) {
    throw new Error('useCourseEditorState must be used within a CourseEditorProvider');
  }
  return context;
};
